import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft, ShieldCheck, MailCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const Logo = ({ logoUrl, brandName }) =>
  logoUrl ? (
    <img src={logoUrl} alt={brandName} className="h-9 w-9 rounded-xl object-cover bg-white border border-[#ececef]" />
  ) : (
    <img src="/shuttle-logo.png" alt={brandName} className="h-9 w-9 object-contain" />
  );

export default function AuthScreen({ brandName = "Mission Control", logoUrl, deviceScope = "agency", eyebrow = "Welcome back", title = "Sign in", heroTitle, heroSubtitle, heroPosition = "bottom-left", onLogin, onAuthenticated }) {
  const [step, setStep] = useState("credentials"); // credentials | twofa | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [challengeId, setChallengeId] = useState(null);
  const [forgotEmail, setForgotEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const deviceKey = (e) => `mc_device:${deviceScope}:${(e || "").trim().toLowerCase()}`;

  const submitCredentials = async (e) => {
    e?.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = await onLogin(email.trim(), password, localStorage.getItem(deviceKey(email)));
      if (data?.two_factor_required) {
        setChallengeId(data.challenge_id);
        setCode("");
        setStep("twofa");
      } else if (data?.token) {
        await onAuthenticated(data);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e?.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-2fa", { challenge_id: challengeId, code: code.trim(), remember_device: remember });
      if (remember && data.device_token) localStorage.setItem(deviceKey(email), data.device_token);
      await onAuthenticated(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Verification failed");
    } finally { setLoading(false); }
  };

  const resendCode = async () => {
    setError("");
    try {
      const data = await onLogin(email.trim(), password, null);
      if (data?.challenge_id) setChallengeId(data.challenge_id);
      toast.success("A new code is on its way");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const submitForgot = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim() });
      toast.success("If that email exists, we've sent a reset link.");
      setStep("credentials");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const [vpos, hpos] = (heroPosition || "bottom-left").split("-");
  const vClass = vpos === "top" ? "justify-start pt-28" : vpos === "center" ? "justify-center" : "justify-end";
  const alignItems = hpos === "center" ? "items-center" : "items-start";
  const textAlign = hpos === "center" ? "text-center" : "text-left";

  return (
    <div className="min-h-screen flex bg-white">
      <div className="hidden lg:block w-1/2 relative overflow-hidden border-r border-[#f0f0f3] hero-gradient">
        <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-[#DDFD6C]/40 blur-3xl" />
        <div className="absolute bottom-10 -left-16 h-72 w-72 rounded-full bg-[#EAE2FF]/70 blur-3xl" />
        <div className="absolute top-12 left-12 z-20 flex items-center gap-3">
          <Logo logoUrl={logoUrl} brandName={brandName} />
          <span className="font-display text-lg font-semibold tracking-tight">Mission Control</span>
        </div>
        {(heroTitle || heroSubtitle) && (
          <div className={`absolute inset-0 z-10 p-14 flex flex-col ${vClass} ${alignItems}`}>
            <div className={`max-w-md ${textAlign} animate-fade-up`}>
              {heroTitle && <h2 className="font-display text-4xl xl:text-5xl font-semibold tracking-tight leading-[1.05] text-[#06060C]">{heroTitle}</h2>}
              {heroSubtitle && <p className="mt-5 text-[#595D62] max-w-sm text-[15px] leading-relaxed">{heroSubtitle}</p>}
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm animate-fade-up" key={step}>
          <div className="lg:hidden mb-10 flex items-center gap-3"><Logo logoUrl={logoUrl} brandName={brandName} /><span className="font-display text-lg font-semibold tracking-tight">Mission Control</span></div>

          {step === "credentials" && (
            <>
              <p className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-[#723FFF] mb-2.5">{eyebrow}</p>
              <h1 className="font-display text-3xl font-semibold tracking-tight mb-9 text-[#06060C]">{title}</h1>
              <form onSubmit={submitCredentials} className="space-y-4">
                <div>
                  <label className="text-[11px] text-[#9FA1A4] mb-1.5 block font-mono-data uppercase tracking-wider">Email</label>
                  <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="bg-[#F6F6FD] border-[#EEEFEF] h-12 rounded-2xl px-4 focus-visible:ring-[#723FFF]/30" required />
                </div>
                <div>
                  <label className="text-[11px] text-[#9FA1A4] mb-1.5 block font-mono-data uppercase tracking-wider">Password</label>
                  <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="bg-[#F6F6FD] border-[#EEEFEF] h-12 rounded-2xl px-4 focus-visible:ring-[#723FFF]/30" required />
                </div>
                {error && <p data-testid="login-error" className="text-sm text-[#d83a3f]">{error}</p>}
                <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full h-12 bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full font-medium transition-all hover:scale-[1.01] mt-2">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="ml-1 h-4 w-4" /></>}
                </Button>
                <button type="button" data-testid="forgot-password-link" onClick={() => { setForgotEmail(email); setStep("forgot"); setError(""); }} className="w-full text-center text-sm text-[#9FA1A4] hover:text-[#723FFF] transition-colors mt-2">
                  Forgot password?
                </button>
              </form>
            </>
          )}

          {step === "twofa" && (
            <>
              <div className="h-11 w-11 rounded-2xl bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center mb-5"><ShieldCheck className="h-5 w-5" /></div>
              <h1 className="font-display text-3xl font-semibold tracking-tight mb-2 text-[#06060C]">Check your email</h1>
              <p className="text-sm text-[#9FA1A4] mb-8 leading-relaxed">We sent a 6-digit code to <span className="text-[#06060C] font-medium">{email}</span>. Enter it below to finish signing in.</p>
              <form onSubmit={submitCode} className="space-y-4">
                <Input data-testid="twofa-code-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="bg-[#F6F6FD] border-[#EEEFEF] h-14 rounded-2xl text-center text-2xl tracking-[0.5em] font-mono-data focus-visible:ring-[#723FFF]/30" required autoFocus />
                <label className="flex items-center gap-2.5 cursor-pointer select-none py-1">
                  <Checkbox data-testid="twofa-remember" checked={remember} onCheckedChange={(v) => setRemember(!!v)} className="border-[#c4c5cc] data-[state=checked]:bg-[#06060C] data-[state=checked]:border-[#06060C]" />
                  <span className="text-sm text-[#595D62]">Remember this device for 14 days</span>
                </label>
                {error && <p data-testid="twofa-error" className="text-sm text-[#d83a3f]">{error}</p>}
                <Button data-testid="twofa-submit-button" type="submit" disabled={loading || code.length < 6} className="w-full h-12 bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full font-medium transition-all hover:scale-[1.01]">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Verify & sign in <ArrowRight className="ml-1 h-4 w-4" /></>}
                </Button>
                <div className="flex items-center justify-between pt-1">
                  <button type="button" onClick={() => { setStep("credentials"); setError(""); }} className="flex items-center gap-1 text-sm text-[#9FA1A4] hover:text-[#06060C]"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
                  <button type="button" data-testid="twofa-resend" onClick={resendCode} className="text-sm text-[#9FA1A4] hover:text-[#723FFF]">Resend code</button>
                </div>
              </form>
            </>
          )}

          {step === "forgot" && (
            <>
              <div className="h-11 w-11 rounded-2xl bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center mb-5"><MailCheck className="h-5 w-5" /></div>
              <h1 className="font-display text-3xl font-semibold tracking-tight mb-2 text-[#06060C]">Reset password</h1>
              <p className="text-sm text-[#9FA1A4] mb-8 leading-relaxed">Enter your email and we'll send you a link to set a new password.</p>
              <form onSubmit={submitForgot} className="space-y-4">
                <Input data-testid="forgot-email-input" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="you@email.com" className="bg-[#F6F6FD] border-[#EEEFEF] h-12 rounded-2xl px-4 focus-visible:ring-[#723FFF]/30" required autoFocus />
                <Button data-testid="forgot-submit-button" type="submit" disabled={loading} className="w-full h-12 bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full font-medium">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
                </Button>
                <button type="button" onClick={() => setStep("credentials")} className="w-full flex items-center justify-center gap-1 text-sm text-[#9FA1A4] hover:text-[#06060C] mt-1"><ArrowLeft className="h-3.5 w-3.5" /> Back to sign in</button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
